import { CHTooltip } from '@/components/tooltip';
import { EnumButtonName } from '@calm/evexia-analytics';
import { useAudio } from '@rhg/hooks/use-audio';
import playerMessages from '@rhg/lang/messages/player';
import {
    IconButton,
    IconButtonProps,
    SxProps,
    Theme,
} from '@rhg/libs/material';
import {
    InfoFilled,
    PauseCtaFilled,
    PlayCtaFilled,
} from '@rhg/libs/material/icons';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { ControlButtonType } from '../../../video-player/types';

export interface PlayPauseButtonProps {
    sx?: SxProps<Theme>;
    size?: IconButtonProps['size'];
    onControlClick?: (buttonType: ControlButtonType) => void;
}

export function PlayPauseButton({
    sx,
    size,
    onControlClick,
}: PlayPauseButtonProps) {
    const { formatMessage } = useIntl();
    const [isTooltipOpen, setIsTooltipOpen] = useState(true);
    const {
        controls: { isPaused, togglePlay },
        hasAutoPlayRestriction,
    } = useAudio();

    const handleTogglePlay = useCallback(() => {
        if (isPaused && hasAutoPlayRestriction) {
            setIsTooltipOpen(false);
        }
        if (onControlClick) {
            // Reverse of current state, prior to togglePlay
            onControlClick(isPaused ? 'play' : 'pause');
        }
        togglePlay();
    }, [hasAutoPlayRestriction, isPaused, onControlClick, togglePlay]);

    const button = (
        <IconButton
            trackButton={
                isPaused
                    ? EnumButtonName.AudioPlayerPlay
                    : EnumButtonName.AudioPlayerPause
            }
            onClick={handleTogglePlay}
            aria-label={
                isPaused
                    ? formatMessage(playerMessages['player.play'])
                    : formatMessage(playerMessages['player.pause'])
            }
            size={size}
            sx={sx}
        >
            {isPaused ? <PlayCtaFilled /> : <PauseCtaFilled />}
        </IconButton>
    );

    if (hasAutoPlayRestriction) {
        return (
            <CHTooltip
                isOpen={isTooltipOpen}
                startIcon={<InfoFilled />}
                topLabel={formatMessage(
                    playerMessages['player.autoplay_error.top_label']
                )}
                content={formatMessage(
                    playerMessages['player.autoplay_error.content']
                )}
            >
                {button}
            </CHTooltip>
        );
    }
    return button;
}
